import React from 'react';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import UserAvatar from '../user-avatar/UserAvatar';
import timeAgo from '../../Util/timeAgo';
import { chatAvatarSx, chatColors, chatRadii, chatSizes } from './chatTheme';

/**
 * One reply row: the same structure as a top-level comment at a smaller scale,
 * nested inside the parent's rule-bordered container.
 *
 * Replies never carry tags - they inherit the parent's scope - so there is no
 * chip row here, only name, time and body.
 *
 * "Reply" on a reply still attaches to the *parent*: threads are one level
 * deep. What it does change is where the parent's one field opens - directly
 * under this row - and that it opens pre-filled with "@FirstName ".
 */
export default function ({ name, message, time, now = Date.now(), isAdmin = false, onReply = null }) {
    return (
        <Box sx={{ display: 'flex', gap: 1.25, py: 1 }}>
            <UserAvatar
                name={name}
                size={chatSizes.replyAvatar}
                maxInitials={2}
                sx={[{ flex: '0 0 auto', fontSize: 12 }, chatAvatarSx(isAdmin)]}
            />
            <Box sx={{ flex: '1 1 auto', minWidth: 0 }}>
                <Box
                    sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        alignItems: 'center',
                        gap: 0.75,
                        mb: '2px'
                    }}>
                    <Typography
                        component='span'
                        sx={{ fontSize: 12, fontWeight: 500, lineHeight: 1.3, color: 'text.primary' }}>
                        {name}
                    </Typography>
                    <Tooltip
                        title={time.toLocaleString()}
                        placement='top-start'
                        arrow
                        enterTouchDelay={0}
                        leaveTouchDelay={3000}>
                        <Typography
                            component='span'
                            sx={{ fontSize: 11, lineHeight: 1.3, color: 'text.secondary' }}>
                            {timeAgo(time, now)}
                        </Typography>
                    </Tooltip>
                </Box>
                <Typography
                    sx={{
                        fontSize: 13,
                        lineHeight: 1.45,
                        color: 'text.primary',
                        textWrap: 'pretty',
                        overflowWrap: 'anywhere',
                        whiteSpace: 'pre-wrap'
                    }}>
                    {message}
                </Typography>
                {onReply && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                        <ButtonBase
                            onClick={onReply}
                            sx={(theme) => ({
                                fontSize: 11,
                                fontWeight: 500,
                                padding: '5px 7px',
                                borderRadius: chatRadii.action,
                                color: chatColors(theme).textMuted,
                                '&:hover': { backgroundColor: chatColors(theme).hoverSurface }
                            })}>
                            Reply
                        </ButtonBase>
                    </Box>
                )}
            </Box>
        </Box>
    );
}
